import { useEffect, useRef, useState } from "react";
import { FiMoreVertical } from "react-icons/fi";

export interface CardAction {
  kind: "edit" | "delete" | "duplicate";
  label: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
}

interface CardActionMenuProps {
  name: string;
  menuLabel: string;
  actions: CardAction[];
  triggerClassName?: string;
}

export const CardActionMenu = ({ name, menuLabel, actions, triggerClassName = "text-[var(--ds-on-card)]" }: CardActionMenuProps) => {
  const menuRef = useRef<HTMLDetailsElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;

    const closeOnOutsidePointer = (event: globalThis.PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      menuRef.current?.querySelector("summary")?.focus();
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  return (
    <details
      ref={menuRef}
      open={menuOpen}
      onToggle={(event) => setMenuOpen(event.currentTarget.open)}
      className="relative"
    >
      <summary
        aria-label={menuLabel}
        aria-expanded={menuOpen}
        className={`app-focus-ring flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-full [&::-webkit-details-marker]:hidden ${triggerClassName}`}
      >
        <FiMoreVertical className="h-5 w-5" aria-hidden="true" />
      </summary>
      <div role="group" aria-label={menuLabel} className="absolute right-0 top-11 z-40 min-w-48 rounded-[var(--ds-radius-md)] border border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2 text-[var(--ds-ink)] shadow-[var(--ds-shadow-2)]">
        {actions.map((action) => (
          <button key={action.kind} type="button" aria-label={`${action.label} ${name}`} disabled={action.disabled || action.loading} onClick={(event) => { event.preventDefault(); event.stopPropagation(); setMenuOpen(false); void action.onClick(); }} className={`flex min-h-11 w-full items-center rounded-[var(--ds-radius-sm)] px-3 text-left text-[15px] disabled:opacity-50 ${action.kind === "delete" ? "text-[var(--ds-negative)]" : "text-[var(--ds-ink)]"}`}>
            {action.label}
          </button>
        ))}
      </div>
    </details>
  );
};
